import type { ArenaSelectedSeat } from './arenaSeatSelectionUtils'
import { patchArenaZoomControls } from './arenaSeatSelectionUtils'

const SVG_NS = 'http://www.w3.org/2000/svg'
const OWNED_VISUAL = 'data-le-arena-visual'

export interface ArenaSeatAnchor {
  uuid: string
  element: SVGElement
}

interface Options {
  getSeats: () => ReadonlyMap<string, ArenaSelectedSeat>
  getSelectedIds: () => ReadonlySet<string>
  getSeatLabel: (seat: ArenaSelectedSeat) => string
  checkIcon: SVGElement
  onHover: (anchor: ArenaSeatAnchor | undefined) => void
  onLeave: () => void
}

function svgElement(tag: string, attributes: Record<string, string | number>) {
  const element = document.createElementNS(SVG_NS, tag)
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, String(value)))
  return element
}

function setAttribute(element: Element, name: string, value: string) {
  if (element.getAttribute(name) !== value) element.setAttribute(name, value)
}

// Compatibility with the pinned Arena v1.0.2 standard circle/nested-SVG seat renderer.
// All owned artwork is a noninteractive sibling; Arena retains its original target.
export function attachArenaSeatPresentation(root: ShadowRoot, options: Options) {
  let frame: number | undefined
  let active: ArenaSeatAnchor | undefined
  let keyboardSeat: string | undefined
  const originals = new Map<SVGElement, Map<string, string | null>>()
  const tooltipStyles = new Map<HTMLElement, string | null>()
  const geometry = new Map<string, { x: number, y: number, radius: number }>()
  const supportedSeats = new Set<string>()
  const style = document.createElement('style')
  style.textContent = `
    :host { position: relative; }
    :host > acl-radial-loader { position: absolute !important; top: 50%; left: 50%; transform: translate(-50%, -50%); }
    :host([data-le-arena-presentation]) .tooltip-area { display: none !important; }
    [${OWNED_VISUAL}] { pointer-events: none; }
    [${OWNED_VISUAL}] > g { transform-box: view-box; transition: transform 120ms ease; }
    [${OWNED_VISUAL}][data-hovered] > g { transform: scale(1.1); }
    [data-le-arena-target]:focus,
    [data-le-arena-target]:active { outline: none; }
    [data-le-arena-target]:focus-visible + [${OWNED_VISUAL}] { filter: drop-shadow(0 0 1px #000); }
    @media (prefers-reduced-motion: reduce) { [${OWNED_VISUAL}] > g { transition: none; } }
  `
  root.append(style)
  root.host.setAttribute('data-le-arena-presentation', '')

  function getVisual(uuid: string) {
    const parent = root.getElementById(`arn-${uuid}`)?.parentElement
    return parent && Array.from(parent.children).find((child): child is SVGElement => child instanceof SVGElement && child.getAttribute(OWNED_VISUAL) === uuid)
  }

  function remember(target: SVGElement) {
    if (originals.has(target)) return
    originals.set(target, new Map(['style', 'tabindex', 'role', 'aria-label', 'aria-pressed'].map(name => [name, target.getAttribute(name)])))
  }

  function restore(target: SVGElement, attributes: Map<string, string | null>) {
    attributes.forEach((value, name) => { if (value === null) target.removeAttribute(name); else target.setAttribute(name, value) })
    target.removeAttribute('data-le-arena-target')
  }

  function close() {
    if (active) getVisual(active.uuid)?.removeAttribute('data-hovered')
    active = undefined
    options.onHover(undefined)
  }

  function resolveTarget(event: Event): ArenaSeatAnchor | undefined {
    const seats = options.getSeats()
    for (const target of event.composedPath()) {
      if (!(target instanceof SVGElement)) continue
      const uuid = target.getAttribute('data-testid')
      if (uuid && seats.has(uuid) && target.id === `arn-${uuid}` && target.hasAttribute('data-le-arena-target')) {
        if (!target.classList.contains('available-seat') && !options.getSelectedIds().has(uuid)) return
        return { uuid, element: target }
      }
    }
  }

  function handleEnter(event: Event) {
    // Arena replaces seat targets during selection. Keep suppression across the
    // replacement until it is decorated, and do not reset it on map backgrounds.
    for (const target of event.composedPath()) {
      if (!(target instanceof SVGElement)) continue
      const uuid = target.getAttribute('data-testid')
      if (uuid && options.getSeats().has(uuid) && target.id === `arn-${uuid}`) {
        root.host.toggleAttribute('data-le-arena-presentation', supportedSeats.has(uuid))
        break
      }
    }
    const next = resolveTarget(event)
    if (event.type === 'pointerover' && (event as PointerEvent).pointerType === 'touch') return
    if (!next) return
    if (active?.uuid !== next.uuid) close()
    active = next
    getVisual(next.uuid)?.setAttribute('data-hovered', '')
    options.onHover(next)
  }

  function handleLeave(event: Event) {
    if (!resolveTarget(event)) return
    if (active) getVisual(active.uuid)?.removeAttribute('data-hovered')
    options.onLeave()
  }

  function handleKey(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      close()
      return
    }
    if (event.key !== 'Enter' && event.key !== ' ') return
    const target = resolveTarget(event)
    if (!target) return
    event.preventDefault()
    keyboardSeat = target.uuid
    target.element.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, composed: true }))
  }

  function patchControlTooltips() {
    root.querySelectorAll('app-navigation button[aria-describedby]').forEach((button) => {
      const id = button.getAttribute('aria-describedby')
      if (!id) return
      const tooltip = root.getElementById(id) ?? document.getElementById(id)
      if (!(tooltip instanceof HTMLElement) || tooltip.tagName.toLowerCase() !== 'app-tooltip-overlay') return
      if (!tooltipStyles.has(tooltip)) tooltipStyles.set(tooltip, tooltip.getAttribute('style'))
      tooltip.style.borderRadius = '4px'
      tooltip.style.fontSize = '12px'
      tooltip.style.lineHeight = '16px'
      tooltip.style.padding = '8px 12px'
    })
    tooltipStyles.forEach((_, tooltip) => { if (!tooltip.isConnected) tooltipStyles.delete(tooltip) })
  }

  function patch() {
    frame = undefined
    const seats = options.getSeats()
    const selectedIds = options.getSelectedIds()
    supportedSeats.clear()
    if (active && (!active.element.isConnected || !seats.has(active.uuid))) close()
    originals.forEach((attributes, target) => {
      if (!target.isConnected) originals.delete(target)
      else if (!seats.has(target.getAttribute('data-testid') ?? '')) {
        restore(target, attributes)
        originals.delete(target)
      }
    })
    root.querySelectorAll(`[${OWNED_VISUAL}]`).forEach((visual) => {
      const uuid = visual.getAttribute(OWNED_VISUAL)!
      if (!seats.has(uuid) || !root.getElementById(`arn-${uuid}`)) visual.remove()
    })

    seats.forEach((seat, uuid) => {
      const target = root.getElementById(`arn-${uuid}`)
      if (!(target instanceof SVGElement) || !target.parentElement) return
      const selected = selectedIds.has(uuid)
      const available = target.classList.contains('available-seat') || selected
      const tag = target.tagName.toLowerCase()
      if (tag === 'circle') {
        geometry.set(uuid, { x: Number(target.getAttribute('cx')), y: Number(target.getAttribute('cy')), radius: Number(target.getAttribute('r')) })
      } else if (tag === 'svg' && selected && !geometry.has(uuid)) {
        const width = Number(target.getAttribute('width'))
        const height = Number(target.getAttribute('height'))
        if (width !== height || !width) return
        geometry.set(uuid, { x: width / 2, y: height / 2, radius: width * 0.4 })
      } else if (tag !== 'svg' || !selected) return
      const dimensions = geometry.get(uuid)
      if (!dimensions || dimensions.radius <= 0) return
      supportedSeats.add(uuid)
      const { x, y, radius } = dimensions
      const colour = target.getAttribute('fill') ?? seat.bgColor ?? '#cccccc'
      const state = selected ? 'selected' : available ? 'available' : 'unavailable'
      const signature = `${state}:${colour}:${seat.isRestrictedView}:${x}:${y}:${radius}`
      let visual = getVisual(uuid)
      if (visual?.getAttribute('data-signature') !== signature || !visual.querySelector('[data-le-seat-fill]')) {
        visual?.remove()
        visual = svgElement('g', { [OWNED_VISUAL]: uuid, 'aria-hidden': 'true', 'data-state': state, 'data-signature': signature })
        const content = svgElement('g', {})
        content.style.transformOrigin = `${x}px ${y}px`
        // Figma sizes 24/12/30 become ratios in the original seat's coordinate system.
        const fill = svgElement('circle', { cx: x, cy: y, r: radius * (selected ? 1.25 : available ? 1 : 0.5), fill: colour, 'data-le-seat-fill': '' })
        if (available) {
          fill.setAttribute('stroke', '#000000')
          fill.setAttribute('stroke-opacity', '0.2')
          fill.setAttribute('stroke-width', String(radius / 6))
        }
        content.append(fill)
        if (seat.isRestrictedView && available) {
          const half = fill.cloneNode(true) as SVGElement
          half.removeAttribute('data-le-seat-fill')
          half.setAttribute('fill', '#000000')
          half.setAttribute('fill-opacity', '0.2')
          half.removeAttribute('stroke')
          half.style.clipPath = 'inset(0 50% 0 0)'
          content.append(half)
        }
        if (selected) {
          content.append(svgElement('circle', { cx: x, cy: y, r: radius, fill: 'none', stroke: '#ffffff', 'stroke-width': radius / 6 }))
          const check = options.checkIcon.cloneNode(true) as SVGElement
          check.setAttribute('x', String(x - radius))
          check.setAttribute('y', String(y - radius))
          check.setAttribute('width', String(radius * 2))
          check.setAttribute('height', String(radius * 2))
          check.style.color = '#ffffff'
          content.append(check)
        }
        visual.append(content)
        target.after(visual)
      }
      remember(target)
      target.setAttribute('data-le-arena-target', '')
      target.style.opacity = '0'
      target.style.pointerEvents = 'all'
      if (available) {
        setAttribute(target, 'tabindex', '0')
        setAttribute(target, 'role', 'button')
        setAttribute(target, 'aria-label', options.getSeatLabel(seat))
        setAttribute(target, 'aria-pressed', String(selected))
      } else {
        target.removeAttribute('tabindex')
        target.removeAttribute('role')
        target.removeAttribute('aria-label')
        target.removeAttribute('aria-pressed')
        if (active?.uuid === uuid) close()
      }
      if (keyboardSeat === uuid) {
        target.focus()
        keyboardSeat = undefined
      }
    })
    patchArenaZoomControls(root)
    patchControlTooltips()
  }

  function refresh() {
    if (frame === undefined) frame = window.requestAnimationFrame(patch)
  }

  const observer = new MutationObserver((records) => {
    // Zoom changes only need control/tooltip updates, not rebuilding all seat artwork.
    if (records.some(record => record.attributeName === 'transform' && record.target === root.querySelector('svg[class~="map-svg"] > g'))) {
      close()
      patchArenaZoomControls(root)
    }
    if (records.some(record => record.attributeName !== 'transform')) refresh()
  })
  observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'fill', 'aria-describedby', 'transform'] })
  root.addEventListener('pointerover', handleEnter)
  root.addEventListener('pointerout', handleLeave)
  root.addEventListener('focusin', handleEnter)
  root.addEventListener('focusout', handleLeave)
  root.addEventListener('keydown', handleKey)
  root.addEventListener('pointerdown', close, true)
  root.addEventListener('wheel', close, { passive: true })
  window.addEventListener('resize', close)
  refresh()

  return {
    refresh,
    destroy() {
      observer.disconnect()
      if (frame !== undefined) window.cancelAnimationFrame(frame)
      root.removeEventListener('pointerover', handleEnter)
      root.removeEventListener('pointerout', handleLeave)
      root.removeEventListener('focusin', handleEnter)
      root.removeEventListener('focusout', handleLeave)
      root.removeEventListener('keydown', handleKey)
      root.removeEventListener('pointerdown', close, true)
      root.removeEventListener('wheel', close)
      window.removeEventListener('resize', close)
      root.querySelectorAll(`[${OWNED_VISUAL}]`).forEach(element => element.remove())
      originals.forEach((attributes, target) => restore(target, attributes))
      tooltipStyles.forEach((value, tooltip) => { if (value === null) tooltip.removeAttribute('style'); else tooltip.setAttribute('style', value) })
      root.host.removeAttribute('data-le-arena-presentation')
      style.remove()
      close()
    },
  }
}
